import { useEffect, useState, type FormEvent } from "react";
import { PROVIDER_PRESETS, type Provider } from "@mailstorm/shared";
import { api } from "../../lib/api";

export function AccountWizard({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [tab, setTab] = useState<"oauth" | "imap">("oauth");
  const [provider, setProvider] = useState<Provider>("gmail");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [imapHost, setImapHost] = useState(PROVIDER_PRESETS.gmail.imap.host);
  const [imapPort, setImapPort] = useState(PROVIDER_PRESETS.gmail.imap.port);
  const [smtpHost, setSmtpHost] = useState(PROVIDER_PRESETS.gmail.smtp.host);
  const [smtpPort, setSmtpPort] = useState(PROVIDER_PRESETS.gmail.smtp.port);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (provider === "custom") return;
    const p = PROVIDER_PRESETS[provider];
    setImapHost(p.imap.host);
    setImapPort(p.imap.port);
    setSmtpHost(p.smtp.host);
    setSmtpPort(p.smtp.port);
  }, [provider]);

  async function startOAuth(p: "gmail" | "microsoft") {
    setError("");
    setBusy(true);
    try {
      const { url } = await api.oauthStart(p);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function addImap(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.addImap({
        email,
        provider,
        password,
        imap: { host: imapHost, port: Number(imapPort), secure: Number(imapPort) === 993 },
        smtp: { host: smtpHost, port: Number(smtpPort), secure: Number(smtpPort) === 465 },
        color: provider === "gmail" ? "#ea4335" : provider === "microsoft" ? "#0078d4" : "#c4a574",
      });
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)" }}>Add account</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <p style={{ color: "var(--muted)" }}>
          Connect Gmail / Microsoft with OAuth, or any IMAP mailbox with app password.
        </p>
        <div className="actions" style={{ marginBottom: "1rem" }}>
          <button className={`btn ${tab === "oauth" ? "btn-primary" : ""}`} onClick={() => setTab("oauth")}>
            OAuth
          </button>
          <button className={`btn ${tab === "imap" ? "btn-primary" : ""}`} onClick={() => setTab("imap")}>
            IMAP / SMTP
          </button>
        </div>

        {tab === "oauth" ? (
          <div className="form">
            <button className="btn btn-primary" disabled={busy} onClick={() => void startOAuth("gmail")}>
              Continue with Google
            </button>
            <button className="btn" disabled={busy} onClick={() => void startOAuth("microsoft")}>
              Continue with Microsoft
            </button>
            <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
              Configure `GOOGLE_CLIENT_ID` / `MICROSOFT_CLIENT_ID` in server `.env` if these buttons error.
            </p>
            {error && <div className="error">{error}</div>}
          </div>
        ) : (
          <form className="form" onSubmit={addImap}>
            <label className="label">Provider preset</label>
            <select className="select" value={provider} onChange={(e) => setProvider(e.target.value as Provider)}>
              {Object.entries(PROVIDER_PRESETS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
              <option value="custom">Custom</option>
            </select>
            <label className="label">Email</label>
            <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <label className="label">Password / App password</label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: "0.5rem" }}>
              <input className="input" value={imapHost} onChange={(e) => setImapHost(e.target.value)} />
              <input
                className="input"
                type="number"
                value={imapPort}
                onChange={(e) => setImapPort(Number(e.target.value))}
              />
              <input className="input" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
              <input
                className="input"
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(Number(e.target.value))}
              />
            </div>
            {error && <div className="error">{error}</div>}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? "Testing…" : "Test & save"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
